import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import {
  AddLocation as AddLocationIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  DriveFileMove as DriveFileMoveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useUser } from 'components/RDAuthContext';
import { historyService } from '../services/domain'; // UPDATED: Using historyService

// Entity Type mappings for consistent display and filtering
const ENTITY_TYPE_MAPPINGS = {
  // Display Name -> Database Table Name (what comes from the service)
  'Location': 'Location',
  'Org Unit': 'OrgUnit',
  'Labor Targets': 'LocationLaborTargets',
  'Manager Assignment': 'ManagerAssignment',
  'Hierarchy Structure': 'HierarchyDefinition',
  'Unknown': 'Unknown'  // For rare cases where changeDetails are missing
};

// Reverse mapping for displaying table names as human-friendly names
const TABLE_TO_DISPLAY_NAME = Object.entries(ENTITY_TYPE_MAPPINGS).reduce((acc, [display, table]) => {
  acc[table] = display;
  return acc;
}, {});

function HistoryTab() {
  const { user } = useUser();
  const [historyData, setHistoryData] = useState({ items: [], totalCount: 0, hasMore: false });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    entityType: '',
    entityName: '',  // Changed from entityId to entityName
    changedBy: '',
    dateFrom: null,
    dateTo: null,
    changeType: '',
    searchTerm: '',
    limit: 100
  });
  const [page, setPage] = useState(1);
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set()); // Track expanded items for details

  // Fetch history data
  const fetchHistory = async () => {
    if (!user?.client) return;
    
    setLoading(true);
    try {
      // Convert dateFrom/dateTo to dateRange format expected by service
      const serviceFilters = { ...filters };
      
      // Handle date range conversion
      if (filters.dateFrom || filters.dateTo) {
        serviceFilters.dateRange = {
          start: filters.dateFrom || null,
          end: filters.dateTo || null
        };
        // Remove the individual date fields as service doesn't use them
        delete serviceFilters.dateFrom;
        delete serviceFilters.dateTo;
      }
      
      const result = await historyService.getAllHistory(user.client, serviceFilters);
      setHistoryData(result);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryData({ items: [], totalCount: 0, hasMore: false });
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchHistory();
  }, [user?.client]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPage(1);
    fetchHistory();
  };

  // Handle search clear - immediately fetch with cleared search
  const handleSearchClear = () => {
    setFilters(prev => ({ ...prev, searchTerm: '' }));
    // Fetch history with cleared search term by passing updated filters directly
    const clearedFilters = { ...filters, searchTerm: '' };
    setPage(1);
    historyService.getAllHistory(user.client, clearedFilters).then(result => {
      setHistoryData(result);
    }).catch(error => {
      console.error('Error fetching history:', error);
      setHistoryData({ items: [], totalCount: 0, hasMore: false });
    });
  };

  // Clear filters and immediately fetch with cleared filters
  const handleClearFilters = () => {
    const clearedFilters = {
      entityType: '',
      entityName: '',  // Changed from entityId to entityName
      changedBy: '',
      dateFrom: null,
      dateTo: null,
      changeType: '',
      searchTerm: '',
      limit: 100
    };
    setFilters(clearedFilters);
    setPage(1);
    
    // Immediately fetch history with cleared filters
    setLoading(true);
    historyService.getAllHistory(user.client, clearedFilters).then(result => {
      setHistoryData(result);
    }).catch(error => {
      console.error('Error fetching history:', error);
      setHistoryData({ items: [], totalCount: 0, hasMore: false });
    }).finally(() => {
      setLoading(false);
    });
  };

  // Export history
  const handleExport = () => {
    const csv = [
      ['Date', 'Type', 'Entity', 'Description', 'Changed By', 'Reason'],
      ...historyData.items.map(item => [
        format(parseISO(item.effectiveDate || item.changedAt), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        item.title,
        item.entityName,
        item.description,
        item.changedByName || item.changedBy || 'System',
        item.changeReason || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
  };

  // Group history by date and sort chronologically within each date
  const groupedHistory = useMemo(() => {
    const groups = {};
    
    // First, sort all items by date (newest first)
    const sortedItems = [...historyData.items].sort((a, b) => {
      const dateA = parseISO(a.effectiveDate || a.changedAt);
      const dateB = parseISO(b.effectiveDate || b.changedAt);
      return dateB - dateA; // Newest first
    });
    
    // Group by date
    sortedItems.forEach(item => {
      const date = parseISO(item.effectiveDate || item.changedAt);
      const dateKey = format(date, 'EEEE, MMMM d, yyyy'); // More readable date format
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });
    
    // Sort items within each date group chronologically (newest first)
    Object.keys(groups).forEach(dateKey => {
      groups[dateKey].sort((a, b) => {
        const timeA = parseISO(a.effectiveDate || a.changedAt);
        const timeB = parseISO(b.effectiveDate || b.changedAt);
        return timeB - timeA; // Newest first within the day
      });
    });
    
    return groups;
  }, [historyData.items]);

  // Get icon component for history type
  const getIconComponent = (icon) => {
    const iconMap = {
      'drive_file_move': DriveFileMoveIcon,
      'person': PersonIcon,
      'schedule': ScheduleIcon,
      'add_location': AddLocationIcon,
      'edit': EditIcon,
      'delete': DeleteIcon,
      'history': HistoryIcon,
      'store': StoreIcon
    };
    return iconMap[icon] || HistoryIcon;
  };

  // Format relative time using date-fns
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = parseISO(dateString);
      
      if (isToday(date)) {
        return `Today at ${format(date, 'h:mm a')}`;
      }
      
      if (isYesterday(date)) {
        return 'Yesterday';
      }
      
      // For recent dates, show relative time with more detail
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  };

  // Active filter count
  const activeFilterCount = Object.values(filters).filter(v => v && v !== '').length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight="medium">
                    System History
                  </Typography>
                  <Typography variant="body2" color="text">
                    {historyData.totalCount} total changes recorded
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <AdminButton
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchHistory}
                    disabled={loading}
                  >
                    Refresh
                  </AdminButton>
                  <AdminButton
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    disabled={historyData.items.length === 0}
                  >
                    Export
                  </AdminButton>
                </Box>
              </Box>

              {/* Enhanced Search Bar */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search by location, user, description, or any keyword..."
                  value={filters.searchTerm}
                  onChange={(e) => {
                    handleFilterChange('searchTerm', e.target.value);
                    // Auto-refresh when search is cleared by keyboard
                    if (e.target.value === '' && filters.searchTerm !== '') {
                      setTimeout(() => fetchHistory(), 0);
                    }
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: filters.searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={handleSearchClear}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                />
              </Box>

              {/* Filters */}
              <Accordion expanded={expandedFilters} onChange={() => setExpandedFilters(!expandedFilters)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Badge badgeContent={activeFilterCount} color="primary">
                      <FilterListIcon />
                    </Badge>
                    <Typography>Advanced Filters</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Entity Type</InputLabel>
                        <Select
                          value={filters.entityType}
                          onChange={(e) => handleFilterChange('entityType', e.target.value)}
                          label="Entity Type"
                        >
                          <MenuItem value="">All</MenuItem>
                          {Object.entries(ENTITY_TYPE_MAPPINGS).map(([displayName, tableName]) => (
                            <MenuItem key={tableName} value={tableName}>
                              {displayName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Entity Name"
                        placeholder="Search by name..."
                        value={filters.entityName}
                        onChange={(e) => handleFilterChange('entityName', e.target.value)}
                        helperText="Partial match supported"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Changed By"
                        placeholder="User name..."
                        value={filters.changedBy}
                        onChange={(e) => handleFilterChange('changedBy', e.target.value)}
                        helperText="Partial match supported"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Change Type</InputLabel>
                        <Select
                          value={filters.changeType}
                          onChange={(e) => handleFilterChange('changeType', e.target.value)}
                          label="Change Type"
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="ASSIGNMENT">Assignment</MenuItem>
                          <MenuItem value="CREATION">Creation</MenuItem>
                          <MenuItem value="UPDATE">Update</MenuItem>
                          <MenuItem value="DELETION">Deletion</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Date From"
                        value={filters.dateFrom}
                        onChange={(value) => handleFilterChange('dateFrom', value)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Date To"
                        value={filters.dateTo}
                        onChange={(value) => handleFilterChange('dateTo', value)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Limit</InputLabel>
                        <Select
                          value={filters.limit}
                          onChange={(e) => handleFilterChange('limit', e.target.value)}
                          label="Limit"
                        >
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                          <MenuItem value={250}>250</MenuItem>
                          <MenuItem value={500}>500</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                        <AdminButton
                          variant="contained"
                          onClick={handleApplyFilters}
                          disabled={loading}
                          fullWidth
                        >
                          Apply
                        </AdminButton>
                        <AdminButton
                          variant="outlined"
                          onClick={handleClearFilters}
                          disabled={loading}
                          fullWidth
                        >
                          Clear
                        </AdminButton>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Card>
          </Grid>

          {/* History List */}
          <Grid item xs={12}>
            {loading ? (
              <Card sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              </Card>
            ) : historyData.items.length === 0 ? (
              <Card sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No history found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters or search terms
                  </Typography>
                </Box>
              </Card>
            ) : (
              <>
                {Object.entries(groupedHistory).map(([date, items], dateIndex) => {
                  // Parse the date to check if it's today or yesterday
                  const dateObj = parseISO(items[0].effectiveDate || items[0].changedAt);
                  const isCurrentDay = isToday(dateObj);
                  const isYesterdayDate = isYesterday(dateObj);
                  
                  return (
                    <Card key={date} sx={{ mb: 3, overflow: 'visible' }}>
                      <Box 
                        sx={{ 
                          p: 2.5, 
                          backgroundColor: isCurrentDay ? 'primary.lighter' : 'action.hover',
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon 
                            sx={{ 
                              fontSize: 20, 
                              color: isCurrentDay ? 'primary.main' : 'text.secondary' 
                            }} 
                          />
                          <Typography 
                            variant="subtitle1" 
                            fontWeight="600"
                            color={isCurrentDay ? 'primary.main' : 'text.primary'}
                          >
                            {isCurrentDay ? 'Today' : isYesterdayDate ? 'Yesterday' : date}
                          </Typography>
                          <Chip 
                            label={`${items.length} ${items.length === 1 ? 'change' : 'changes'}`}
                            size="small"
                            color={isCurrentDay ? 'primary' : 'default'}
                            variant={isCurrentDay ? 'filled' : 'outlined'}
                          />
                        </Box>
                      </Box>
                      <List>
                      {items.map((item, itemIndex) => {
                        const IconComponent = getIconComponent(item.icon);
                        const itemTime = parseISO(item.effectiveDate || item.changedAt);
                        const timeDisplay = format(itemTime, 'h:mm a');
                        const isExpanded = expandedItems.has(item.id);
                        
                        return (
                          <ListItem
                            key={item.id}
                            divider={itemIndex < items.length - 1}
                            sx={{
                              '&:hover': { backgroundColor: 'action.hover' },
                              borderLeft: item.isActive ? '4px solid' : 'none',
                              borderLeftColor: item.isActive ? 'primary.main' : 'transparent',
                              position: 'relative',
                              flexDirection: 'column',
                              alignItems: 'stretch'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                              <ListItemIcon>
                                <IconComponent color={item.iconColor || 'inherit'} />
                              </ListItemIcon>
                              <ListItemText
                                sx={{ flex: 1 }}
                                secondaryTypographyProps={{ component: 'div' }}
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="500"
                                      sx={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        flex: '1 1 auto'
                                      }}
                                    >
                                      {item.title}
                                    </Typography>
                                    {/* Show entityType chip with time next to it */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={
                                          TABLE_TO_DISPLAY_NAME[item.entityType] || 
                                          (item.entityType === 'ManagerAssignment' ? 'Manager Assignment' : item.entityType) || 
                                          'Location'
                                        }
                                        size="small"
                                        variant="outlined"
                                        color={
                                          item.entityType === 'Location' ? 'primary' : 
                                          item.entityType === 'LocationLaborTargets' ? 'secondary' : 
                                          item.entityType === 'OrgUnit' ? 'info' :
                                          item.entityType === 'ManagerAssignment' ? 'success' :
                                          item.entityType === 'HierarchyDefinition' ? 'warning' :
                                          'default'
                                        }
                                        sx={{ height: 20 }}
                                      />
                                      {/* Time stamp - now consistently positioned */}
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ 
                                          fontWeight: 500,
                                          minWidth: '65px',
                                          textAlign: 'right'
                                        }}
                                      >
                                        {timeDisplay}
                                      </Typography>
                                    </Box>
                                    {item.isActive && (
                                      <Chip
                                        label="Active"
                                        size="small"
                                        color="primary"
                                        sx={{ height: 20 }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    {/* Display field changes - handle both string and array descriptions */}
                                    {Array.isArray(item.description) ? (
                                      <Box component="div">
                                        {item.description.map((line, idx) => (
                                          <Typography 
                                            key={idx} 
                                            variant="body2" 
                                            component="div"
                                            sx={{
                                              wordBreak: 'break-word',
                                              overflowWrap: 'break-word',
                                              whiteSpace: 'pre-wrap'
                                            }}
                                          >
                                            {line}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography 
                                        variant="body2" 
                                        component="div"
                                        sx={{
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                          whiteSpace: 'pre-wrap'
                                        }}
                                      >
                                        {item.description}
                                      </Typography>
                                    )}
                                    
                                    {/* Changed by - more subtle in summary since it's prominent in details */}
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      component="div" 
                                      sx={{ 
                                        mt: 0.5,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      by {item.changedByName || item.changedBy || 'System'}
                                    </Typography>
                                  </Box>
                                }
                              />
                              <Box>
                                <Tooltip title={isExpanded ? "Hide Details" : "View Details"}>
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedItems);
                                      if (isExpanded) {
                                        newExpanded.delete(item.id);
                                      } else {
                                        newExpanded.add(item.id);
                                      }
                                      setExpandedItems(newExpanded);
                                    }}
                                  >
                                    <ExpandMoreIcon 
                                      sx={{ 
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s'
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            
                            {/* Expanded Details Section */}
                            {isExpanded && (
                              <Box 
                                sx={{ 
                                  mt: 2, 
                                  ml: 7, 
                                  p: 2, 
                                  backgroundColor: 'background.default',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
                                  Full Change Details
                                </Typography>
                                
                                {/* Prominent Changed By and Reason section */}
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    mb: 2, 
                                    backgroundColor: 'primary.lighter',
                                    border: '1px solid',
                                    borderColor: 'primary.light'
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                            Changed By
                                          </Typography>
                                          <Typography variant="body1" fontWeight="medium">
                                            {item.changedByName || item.changedBy || 'System'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                          Reason for Change
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                          {item.changeReason || 'No reason provided'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Paper>
                                
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">Entity Type</Typography>
                                    <Typography variant="body2">{TABLE_TO_DISPLAY_NAME[item.entityType] || item.entityType || 'N/A'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">Entity Name</Typography>
                                    <Typography variant="body2">{item.entityName || 'N/A'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">Entity ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.entityId || 'N/A'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="caption" color="text.secondary">Change Type</Typography>
                                    <Typography variant="body2">{item.changeType || 'N/A'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={8}>
                                    <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                                    <Typography variant="body2">
                                      {item.effectiveDate || item.changedAt ? 
                                        format(parseISO(item.effectiveDate || item.changedAt), 'PPpp') : 
                                        'N/A'
                                      }
                                    </Typography>
                                  </Grid>
                                  {/* Affected Locations */}
                                  {item.affectedLocations && item.affectedLocations.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">Affected Locations</Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {item.affectedLocations.map((location, idx) => {
                                          // Handle both old format (string) and new format (object with id/name)
                                          const locId = typeof location === 'string' ? location : location.id;
                                          const locName = typeof location === 'string' ? location : location.name;
                                          const displayLabel = locName === locId ? locId : `${locName} (${locId})`;
                                          
                                          return (
                                            <Chip
                                              key={idx}
                                              label={displayLabel}
                                              size="small"
                                              variant="outlined"
                                              sx={{ 
                                                height: 24,
                                                fontSize: '0.75rem',
                                                '& .MuiChip-label': {
                                                  px: 1
                                                }
                                              }}
                                              title={`ID: ${locId}`}
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Grid>
                                  )}
                                  {/* Affected Org Units */}
                                  {item.affectedOrgUnits && item.affectedOrgUnits.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">Affected Org Units</Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {item.affectedOrgUnits.map((unit, idx) => {
                                          // Handle both old format (string) and new format (object with id/name)
                                          const unitId = typeof unit === 'string' ? unit : unit.id;
                                          const unitName = typeof unit === 'string' ? unit : unit.name;
                                          const displayLabel = unitName === unitId ? unitId : `${unitName} (${unitId})`;
                                          
                                          return (
                                            <Chip
                                              key={idx}
                                              label={displayLabel}
                                              size="small"
                                              variant="outlined"
                                              color="info"
                                              sx={{ 
                                                height: 24,
                                                fontSize: '0.75rem',
                                                '& .MuiChip-label': {
                                                  px: 1
                                                }
                                              }}
                                              title={`ID: ${unitId}`}
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                                
                                {/* Field Changes */}
                                {item.fieldChanges && item.fieldChanges.length > 0 && (
                                  <Box mt={2}>
                                    <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
                                      Field Changes
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1 }}>
                                      {item.fieldChanges.map((change, idx) => (
                                        <Box key={idx} sx={{ mb: idx < item.fieldChanges.length - 1 ? 1 : 0 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {change.fieldName}:
                                          </Typography>
                                          <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                                            <span style={{ textDecoration: 'line-through', color: 'error.main' }}>
                                              {change.oldDisplayValue || change.oldValue || 'empty'}
                                            </span>
                                            {' → '}
                                            <span style={{ color: 'success.main' }}>
                                              {change.newDisplayValue || change.newValue || 'empty'}
                                            </span>
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Paper>
                                  </Box>
                                )}
                                
                                {/* Additional metadata if available */}
                                {item.metadata && (
                                  <Box mt={2}>
                                    <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
                                      Additional Information
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1 }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {typeof item.metadata === 'string' ? item.metadata : JSON.stringify(item.metadata, null, 2)}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Card>
                  );
                })}

                {/* Pagination */}
                {historyData.hasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Alert severity="info">
                      Showing {historyData.items.length} of {historyData.totalCount} items.
                      Use filters to narrow your search.
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

export default HistoryTab;